import React, { useMemo, useState } from 'react';
import {
  Avatar, Badge, Box, Button, Divider, Flex, HStack, Icon, IconButton, Input,
  InputGroup, InputLeftElement, Select, SimpleGrid, Text, Tooltip, VStack,
} from '@chakra-ui/react';
import {
  CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, CircleX, Clock3, Hourglass, Plus, Search, Users,
} from 'lucide-react';
import { AppointmentStaff, FetchedAppointment, getAppointmentClientName } from '../api/appointmentApi';

interface AppointmentCalendarProps {
  appointments: FetchedAppointment[];
  onAppointmentClick?: (appointment: FetchedAppointment) => void;
  onDayClick?: (date: Date) => void;
}

type StatusKey = 'pending' | 'confirmed' | 'cancelled' | 'done';

const TIMEZONE = 'Africa/Casablanca';
const GRID_START_HOUR = 8;
const GRID_END_HOUR = 21;
const PIXELS_PER_MINUTE = 1.4;

const statusMeta: Record<StatusKey, { label: string; colorScheme: string; bg: string; border: string; text: string }> = {
  pending: { label: 'En attente', colorScheme: 'yellow', bg: 'yellow.50', border: 'yellow.300', text: 'yellow.900' },
  confirmed: { label: 'Confirmé', colorScheme: 'green', bg: 'green.50', border: 'green.300', text: 'green.900' },
  cancelled: { label: 'Annulé', colorScheme: 'red', bg: 'red.50', border: 'red.300', text: 'red.900' },
  done: { label: 'Terminé', colorScheme: 'gray', bg: 'gray.100', border: 'gray.300', text: 'gray.800' },
};

interface TimeGridEvent {
  appointment: FetchedAppointment;
  startMinutes: number;
  endMinutes: number;
  column: number;
  columnCount: number;
}

const normalizeStatus = (status: string): StatusKey => {
  if (status === 'completed' || status === 'done') return 'done';
  if (status === 'canceled' || status === 'cancelled') return 'cancelled';
  if (status === 'confirmed') return 'confirmed';
  return 'pending';
};

const toDayKey = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const isSameDay = (left: Date, right: Date): boolean => toDayKey(left) === toDayKey(right);

const getWeekStart = (date: Date): Date => {
  const start = new Date(date);
  const weekday = start.getDay();
  start.setDate(start.getDate() + (weekday === 0 ? -6 : 1 - weekday));
  start.setHours(0, 0, 0, 0);
  return start;
};

const getMonthGridStart = (date: Date): Date => {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  return getWeekStart(first);
};

const timeParts = (dateString: string): { year: string; month: string; day: string; hour: number; minute: number } => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(dateString));
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return { year: byType.year, month: byType.month, day: byType.day, hour: Number(byType.hour), minute: Number(byType.minute) };
};

const appointmentDayKey = (appointment: FetchedAppointment): string => {
  const parts = timeParts(appointment.appointment_time);
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const appointmentEnd = (appointment: FetchedAppointment): string => {
  const assignments = appointment.service_assignments || [];
  if (assignments.length > 0) return assignments[assignments.length - 1].ends_at;
  const duration = appointment.services.reduce((total, service) => total + service.duration_minutes, 0);
  return new Date(new Date(appointment.appointment_time).getTime() + Math.max(duration, 15) * 60_000).toISOString();
};

const formatTime = (dateString: string): string => new Intl.DateTimeFormat('fr-MA', {
  timeZone: TIMEZONE, hour: '2-digit', minute: '2-digit', hour12: false,
}).format(new Date(dateString));

const getAppointmentStaff = (appointment: FetchedAppointment): AppointmentStaff[] => {
  const staff = (appointment.service_assignments || []).map((assignment) => assignment.staff).filter(Boolean);
  if (staff.length > 0) return Array.from(new Map(staff.map((member) => [member.id, member])).values());
  return appointment.staff ? [appointment.staff] : [];
};

const initials = (name: string): string => name.split(' ').filter(Boolean).slice(0, 2).map((word) => word[0]).join('').toUpperCase();

const statusIcon = (status: StatusKey) => {
  if (status === 'confirmed') return CheckCircle2;
  if (status === 'cancelled') return CircleX;
  if (status === 'done') return CheckCircle2;
  return Hourglass;
};

const layoutEvents = (appointments: FetchedAppointment[]): TimeGridEvent[] => {
  const events = appointments.map((appointment) => {
    const start = timeParts(appointment.appointment_time);
    const end = timeParts(appointmentEnd(appointment));
    const startMinutes = start.hour * 60 + start.minute;
    return { appointment, startMinutes, endMinutes: Math.max(end.hour * 60 + end.minute, startMinutes + 15) };
  }).sort((left, right) => left.startMinutes - right.startMinutes || left.endMinutes - right.endMinutes);

  const result: TimeGridEvent[] = [];
  let group: Array<{ appointment: FetchedAppointment; startMinutes: number; endMinutes: number; column: number }> = [];
  let groupEnd = -1;
  const flush = () => {
    if (group.length === 0) return;
    const columnCount = Math.max(...group.map((event) => event.column)) + 1;
    result.push(...group.map((event) => ({ ...event, columnCount })));
    group = [];
    groupEnd = -1;
  };

  events.forEach((event) => {
    if (group.length > 0 && event.startMinutes >= groupEnd) flush();
    const occupied = new Set(group.filter((item) => item.endMinutes > event.startMinutes).map((item) => item.column));
    let column = 0;
    while (occupied.has(column)) column += 1;
    group.push({ ...event, column });
    groupEnd = Math.max(groupEnd, event.endMinutes);
  });
  flush();
  return result;
};

const AppointmentCalendar = ({ appointments, onAppointmentClick, onDayClick }: AppointmentCalendarProps) => {
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [visibleStatuses, setVisibleStatuses] = useState<StatusKey[]>(['pending', 'confirmed', 'cancelled', 'done']);

  const weekStart = useMemo(() => getWeekStart(anchorDate), [anchorDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)), [weekStart]);
  const weekEnd = weekDays[6];
  const gridHeight = (GRID_END_HOUR - GRID_START_HOUR) * 60 * PIXELS_PER_MINUTE;
  const hours = Array.from({ length: GRID_END_HOUR - GRID_START_HOUR + 1 }, (_, index) => GRID_START_HOUR + index);

  const staffOptions = useMemo(() => Array.from(new Map(appointments.flatMap(getAppointmentStaff).map((staff) => [staff.id, staff])).values()), [appointments]);
  const inWeek = useMemo(() => {
    const start = toDayKey(weekStart);
    const end = toDayKey(weekEnd);
    return appointments.filter((appointment) => {
      const key = appointmentDayKey(appointment);
      return key >= start && key <= end;
    });
  }, [appointments, weekStart, weekEnd]);

  const filteredAppointments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return inWeek.filter((appointment) => {
      const status = normalizeStatus(appointment.status);
      const staff = getAppointmentStaff(appointment);
      if (!visibleStatuses.includes(status)) return false;
      if (staffFilter && !staff.some((member) => String(member.id) === staffFilter)) return false;
      if (!term) return true;
      const serviceNames = appointment.services.map((service) => service.name).join(' ');
      return `${getAppointmentClientName(appointment)} ${appointment.client.email || ''} ${service.map((member) => member.name).join(' ')} ${serviceNames}`.toLowerCase().includes(term);
    });
  }, [inWeek, searchTerm, staffFilter, visibleStatuses]);

  const eventsByDay = useMemo(() => {
    const byDay: Record<string, TimeGridEvent[]> = {};
    weekDays.forEach((day) => { byDay[toDayKey(day)] = []; });
    filteredAppointments.forEach((appointment) => { byDay[appointmentDayKey(appointment)]?.push(appointment); });
    Object.entries(byDay).forEach(([key, dayAppointments]) => { byDay[key] = layoutEvents(dayAppointments); });
    return byDay;
  }, [filteredAppointments, weekDays]);

  const statusCounts = useMemo(() => {
    const counts: Record<StatusKey, number> = { pending: 0, confirmed: 0, cancelled: 0, done: 0 };
    inWeek.forEach((appointment) => { counts[normalizeStatus(appointment.status)] += 1; });
    return counts;
  }, [inWeek]);

  const hasFilters = searchTerm.length > 0 || staffFilter.length > 0 || visibleStatuses.length !== 4;
  const monthGridStart = useMemo(() => getMonthGridStart(anchorDate), [anchorDate]);
  const monthDays = useMemo(() => Array.from({ length: 42 }, (_, index) => addDays(monthGridStart, index)), [monthGridStart]);
  const formatRange = `${weekStart.toLocaleDateString('fr-MA', { day: 'numeric', month: 'long' })} - ${weekEnd.toLocaleDateString('fr-MA', { day: 'numeric', month: 'long', year: 'numeric' })}`;

  const selectDay = (day: Date) => {
    setSelectedDay(day);
    setAnchorDate(day);
  };

  const toggleStatus = (status: StatusKey) => {
    setVisibleStatuses((current) => {
      if (current.includes(status)) return current.length === 1 ? current : current.filter((item) => item !== status);
      return [...current, status];
    });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStaffFilter('');
    setVisibleStatuses(['pending', 'confirmed', 'cancelled', 'done']);
  };

  return (
    <Box>
      <Flex mb={5} justify="space-between" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={3}>
        <HStack spacing={3}>
          <Flex align="center" justify="center" w={9} h={9} borderRadius="lg" bg="brand.50"><Icon as={CalendarDays} boxSize={4} color="brand.600" /></Flex>
          <Box><Text fontWeight="700" color="gray.900" fontSize="xl">Planning</Text><Text fontSize="sm" color="gray.500">Semaine opérationnelle · heure du Maroc</Text></Box>
        </HStack>
        <Button colorScheme="brand" leftIcon={<Plus size={16} />} onClick={() => onDayClick?.(selectedDay)}>Nouveau rendez-vous</Button>
      </Flex>

      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" overflow="hidden">
        <Flex px={{ base: 4, lg: 6 }} py={4} gap={4} justify="space-between" align={{ base: 'stretch', lg: 'center' }} direction={{ base: 'column', lg: 'row' }} borderBottomWidth="1px" borderColor="gray.100">
          <HStack spacing={1.5}><IconButton aria-label="Semaine précédente" icon={<ChevronLeft size={17} />} size="sm" variant="ghost" onClick={() => setAnchorDate((date) => addDays(date, -7))} /><IconButton aria-label="Semaine suivante" icon={<ChevronRight size={17} />} size="sm" variant="ghost" onClick={() => setAnchorDate((date) => addDays(date, 7))} /><Button size="sm" variant="outline" onClick={() => { const today = new Date(); setAnchorDate(today); setSelectedDay(today); }}>Aujourd&apos;hui</Button><Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="700" color="gray.900" ml={2}>{formatRange}</Text></HStack>
          <Box><Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={1}>Dans le planning</Text><InputGroup size="sm" w={{ base: 'full', lg: '320px' }}><InputLeftElement pointerEvents="none"><Search size={15} color="var(--chakra-colors-gray-400)" /></InputLeftElement><Input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Client, service ou praticien" borderRadius="lg" borderColor="gray.200" /></InputGroup></Box>
        </Flex>

        <Flex px={{ base: 4, lg: 6 }} py={3} gap={2} wrap="wrap" align="center" borderBottomWidth="1px" borderColor="gray.100" bg="gray.25">
          <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider" color="gray.500" mr={1}>Afficher</Text>
          {(Object.keys(statusMeta) as StatusKey[]).map((status) => { const meta = statusMeta[status]; const active = visibleStatuses.includes(status); return <Button key={status} size="xs" variant={active ? 'solid' : 'outline'} borderRadius="full" colorScheme={active ? meta.colorScheme : 'gray'} bg={active ? meta.bg : 'white'} color={active ? meta.text : 'gray.600'} borderColor={active ? `${meta.colorScheme}.200` : 'gray.200'} onClick={() => toggleStatus(status)} aria-pressed={active}>{meta.label}<Badge ml={1.5} borderRadius="full" colorScheme={active ? meta.colorScheme : 'gray'}>{statusCounts[status]}</Badge></Button>; })}
          <Select aria-label="Filtrer par praticien" size="xs" w="180px" borderRadius="full" bg="white" value={staffFilter} onChange={(event) => setStaffFilter(event.target.value)}><option value="">Toute l&apos;équipe</option>{staffOptions.map((staff) => <option key={staff.id} value={staff.id}>{staff.name}</option>)}</Select>
          {hasFilters && <Button size="xs" variant="ghost" color="brand.700" onClick={resetFilters}>Réinitialiser</Button>}
        </Flex>

        <Flex direction={{ base: 'column', xl: 'row' }} align="stretch">
          <Box w={{ base: 'full', xl: '230px' }} flexShrink={0} p={4} borderRightWidth={{ base: 0, xl: '1px' }} borderBottomWidth={{ base: '1px', xl: 0 }} borderColor="gray.100" bg="gray.25">
            <Flex align="center" justify="space-between" mb={4}><Text fontSize="sm" fontWeight="700" color="gray.800" textTransform="capitalize">{anchorDate.toLocaleDateString('fr-MA', { month: 'long', year: 'numeric' })}</Text><HStack spacing={0.5}><IconButton aria-label="Mois précédent" icon={<ChevronLeft size={14} />} size="xs" variant="ghost" onClick={() => setAnchorDate((date) => new Date(date.getFullYear(), date.getMonth() - 1, 1))} /><IconButton aria-label="Mois suivant" icon={<ChevronRight size={14} />} size="xs" variant="ghost" onClick={() => setAnchorDate((date) => new Date(date.getFullYear(), date.getMonth() + 1, 1))} /></HStack></Flex>
            <SimpleGrid columns={7} gap={1} mb={1.5}>{['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((label, index) => <Text key={`${label}-${index}`} fontSize="2xs" color="gray.400" textAlign="center" fontWeight="700">{label}</Text>)}</SimpleGrid>
            <SimpleGrid columns={7} gap={1}>{monthDays.map((day) => { const selected = isSameDay(day, selectedDay); const currentMonth = day.getMonth() === anchorDate.getMonth(); return <Button key={toDayKey(day)} size="xs" h="29px" px={0} minW="29px" variant={selected ? 'solid' : 'ghost'} colorScheme={selected ? 'brand' : 'gray'} color={currentMonth ? undefined : 'gray.300'} onClick={() => selectDay(day)}>{day.getDate()}</Button>; })}</SimpleGrid>
            <Divider my={5} borderColor="gray.200" />
            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={3}>Équipe</Text>
            <VStack align="stretch" spacing={2}>{staffOptions.length > 0 ? staffOptions.map((staff) => <HStack key={staff.id} spacing={2}><Avatar size="xs" name={staff.name} bg="gray.500" color="white" fontSize="2xs" /><Text fontSize="xs" color="gray.700" noOfLines={1}>{staff.name}</Text></HStack>) : <Text fontSize="xs" color="gray.500">Aucun praticien affecté.</Text>}</VStack>
            <Divider my={5} borderColor="gray.200" />
            <HStack spacing={2}><Icon as={Clock3} boxSize={4} color="brand.600" /><Text fontSize="xs" fontWeight="700" color="gray.700">{inWeek.length} rendez-vous cette semaine</Text></HStack>
            <Text mt={2} fontSize="xs" color="gray.500">La couleur de chaque carte indique son statut. Les avatars identifient les praticiens.</Text>
          </Box>

          <Box flex="1" minW={0} overflow="auto" bg="white">
            <Box minW="1050px">
              <Box display="grid" gridTemplateColumns="58px repeat(7, minmax(140px, 1fr))" borderBottomWidth="1px" borderColor="gray.200" position="sticky" top={0} zIndex={2} bg="white">
                <Box />
                {weekDays.map((day) => { const selected = isSameDay(day, selectedDay); const today = isSameDay(day, new Date()); return <Flex key={toDayKey(day)} minH="74px" px={3} py={3} justify="space-between" align="flex-start" borderLeftWidth="1px" borderColor="gray.100" bg={selected ? 'brand.50' : 'white'}><Box><Text fontSize="2xs" fontWeight="700" letterSpacing="wider" color={today ? 'brand.700' : 'gray.500'}>{day.toLocaleDateString('fr-MA', { weekday: 'short' }).replace('.', '').toUpperCase()}</Text><Text mt={1} w={7} h={7} display="flex" alignItems="center" justifyContent="center" borderRadius="full" fontWeight="700" fontSize="sm" bg={today ? 'brand.500' : 'transparent'} color={today ? 'white' : 'gray.800'}>{day.getDate()}</Text></Box><Tooltip label="Ajouter un rendez-vous"><IconButton aria-label={`Ajouter le ${day.getDate()}`} icon={<Plus size={15} />} size="xs" variant="ghost" color="brand.700" onClick={() => onDayClick?.(day)} /></Tooltip></Flex>; })}
              </Box>
              <Box display="grid" gridTemplateColumns="58px repeat(7, minmax(140px, 1fr))" position="relative">
                <Box position="relative" h={`${gridHeight}px`} bg="gray.25">{hours.map((hour) => <Text key={hour} position="absolute" top={`${(hour - GRID_START_HOUR) * 60 * PIXELS_PER_MINUTE - 8}px`} right={2} fontSize="2xs" color="gray.500">{String(hour).padStart(2, '0')}:00</Text>)}</Box>
                {weekDays.map((day) => {
                  const dayKey = toDayKey(day);
                  const events = eventsByDay[dayKey] || [];
                  const selected = isSameDay(day, selectedDay);
                  return <Box key={dayKey} position="relative" h={`${gridHeight}px`} borderLeftWidth="1px" borderColor="gray.100" bg={selected ? 'brand.50' : 'white'} onClick={() => setSelectedDay(day)} backgroundImage={`repeating-linear-gradient(to bottom, transparent, transparent ${60 * PIXELS_PER_MINUTE - 1}px, var(--chakra-colors-gray-100) ${60 * PIXELS_PER_MINUTE}px)`}>{events.length === 0 && <Button position="absolute" top={3} left={3} right={3} size="xs" variant="ghost" color="gray.600" borderWidth="1px" borderStyle="dashed" borderColor="gray.300" _hover={{ color: 'brand.700', borderColor: 'brand.300', bg: 'brand.50' }} onClick={(event) => { event.stopPropagation(); onDayClick?.(day); }}><Plus size={13} /> Ajouter</Button>}{events.map((event) => { const appointment = event.appointment; const staff = getAppointmentStaff(appointment)[0]; const normalizedStatus = normalizeStatus(appointment.status); const status = statusMeta[normalizedStatus]; const StatusIcon = statusIcon(normalizedStatus); const top = Math.max(0, event.startMinutes - GRID_START_HOUR * 60) * PIXELS_PER_MINUTE; const height = Math.max(62, Math.min(event.endMinutes, GRID_END_HOUR * 60) - Math.max(event.startMinutes, GRID_START_HOUR * 60)) * PIXELS_PER_MINUTE; const width = `calc(${100 / event.columnCount}% - 8px)`; const left = `calc(${(100 / event.columnCount) * event.column}% + 4px)`; const serviceNames = appointment.services.map((service) => service.name).join(', '); return <Box key={appointment.id} as="button" type="button" textAlign="left" position="absolute" zIndex={1} top={`${top}px`} left={left} w={width} h={`${height}px`} minH="62px" p={2.5} overflow="hidden" borderRadius="md" bg={status.bg} boxShadow="sm" borderWidth="1px" borderColor={status.border} _hover={{ zIndex: 3, boxShadow: 'md', transform: 'translateY(-1px)' }} transition="transform 0.15s ease, box-shadow 0.15s ease" onClick={(clickEvent) => { clickEvent.stopPropagation(); onAppointmentClick?.(appointment); }}><HStack justify="space-between" spacing={1}><Text fontSize="2xs" fontWeight="700" color={status.text}>{formatTime(appointment.appointment_time)} - {formatTime(appointmentEnd(appointment))}</Text><Tooltip label={status.label}><Box display="inline-flex" color={status.text}><Icon as={StatusIcon} boxSize={3.5} /></Box></Tooltip></HStack><Text mt={1} fontSize="xs" fontWeight="700" color="gray.900" noOfLines={1}>{getAppointmentClientName(appointment)}</Text><Text mt={0.5} fontSize="2xs" color="gray.700" noOfLines={1}>{serviceNames}</Text><HStack mt={1} spacing={1.5}><Avatar size="2xs" name={staff?.name} bg="gray.500" color="white" fontSize="2xs" /><Text fontSize="2xs" color="gray.600" noOfLines={1}>{staff?.name || 'Non affecté'}</Text></HStack></Box>; })}</Box>;
                })}
              </Box>
            </Box>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default AppointmentCalendar;